import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import workflowService from '../../../services/workflow';
import { API_PATHS } from '../../../utils/constants';
import DocumentViewer from '../../../Components/Common/DocumentViewer';
import { 
  isDocumentField, 
  normalizeDocumentField, 
  extractDocumentUrl, 
  extractFileName, 
  extractUserId, 
  extractDocumentType, 
  createDocumentFieldProps 
} from '../../../utils/documentUtils';
import DocumentFieldRenderer from '../../../Components/WorkflowFields/DocumentFieldRenderer';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventIcon from '@mui/icons-material/Event';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getToken } from '../../../services/auth';
import { FEATURE_WORKFLOW_DOC_GROUPS, FEATURE_WORKFLOW_DOC_GROUPS_UI, FEATURE_WORKFLOW_DOC_SELECTIONS } from '../../../Configurations/FeatureFlags';
import { selectDefaultDocumentsForWorkflow } from '../../../utils/employeeWorkflowUtils';

const WorkflowRequestDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflowRequest, setWorkflowRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // State for file viewer dialog (legacy - will be removed)
  const [fileViewerDialog, setFileViewerDialog] = useState({
    open: false,
    fileName: '',
    fileUrl: '',
    fileType: '',
    loading: false,
    error: null
  });
  
  // State for new document viewer component
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({
    url: '',
    fileName: '',
    uid: '',
    documentType: ''
  });
  
  // Phase 3: Add state for field categorization and editing
  const [activeTab, setActiveTab] = useState('steps'); // Set Steps as default tab
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0); // Add a force update counter

  // Feature: Grouped Employee Documents (read-only panel)
  const [groupedDocs, setGroupedDocs] = useState(null);
  const [groupedDocsLoading, setGroupedDocsLoading] = useState(false);
  const [groupedDocsError, setGroupedDocsError] = useState(null);
  const [defaultDocSelections, setDefaultDocSelections] = useState([]);
  // Phase 3A: interactive selection state derived from defaults
  const [documentSelections, setDocumentSelections] = useState([]);
  // Cache of ticketId -> createdAt (from activity logs 'CREATED')
  const [ticketCreatedMap, setTicketCreatedMap] = useState({});
  
  // State for skip step confirmation dialog
  const [skipStepDialog, setSkipStepDialog] = useState({
    open: false,
    stepId: null,
    stepName: ''
  });

  // Initialize interactive selections when defaults change (first load)
  useEffect(() => {
    if (defaultDocSelections && defaultDocSelections.length > 0 && documentSelections.length === 0) {
      setDocumentSelections(defaultDocSelections);
    }
  }, [defaultDocSelections]);

  // Helpers for selection handling
  const isDocSelected = (docType, groupId, docId) => {
    return documentSelections.some(
      (s) => s.documentType === docType && s.documentGroupId === groupId && s.documentId === docId
    );
  };

  const toggleDocSelection = (docType, groupId, doc) => {
    const docId = doc.id || doc.documentId || doc.filePath || `${docType}-${groupId}-${doc.fileName || 'unknown'}`;
    const selected = isDocSelected(docType, groupId, docId);
    if (selected) {
      setDocumentSelections((prev) =>
        prev.filter((s) => !(s.documentType === docType && s.documentGroupId === groupId && s.documentId === docId))
      );
    } else {
      setDocumentSelections((prev) => [
        ...prev,
        {
          documentType: docType,
          documentGroupId: groupId,
          documentId: docId,
          filePath: doc.filePath || doc.documentUrl || undefined,
          fileName: doc.fileName || doc.name || undefined,
          version: doc.version || undefined,
          isPrimary: doc.isPrimary || false
        }
      ]);
    }
  };

  useEffect(() => {
    fetchWorkflowRequest();
  }, [id]);

  // Helper: detect UUID
  const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

  // Fetch CREATED timestamp from ticket activity logs and cache it
  const fetchTicketCreatedFromHistory = async (ticket) => {
    try {
      if (!ticket) return;
      const uuid = ticket.id && isUuid(ticket.id) ? ticket.id : null;
      const ticketNumber = ticket.ticketNumber;
      const cacheKey = uuid || (ticketNumber ? `num:${ticketNumber}` : null);
      if (!cacheKey) return;
      if (ticketCreatedMap[cacheKey]) return; // already fetched

      const token = getToken();
      if (!token) return;

      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      let resp = null;
      if (uuid) {
        resp = await fetch(`${API_PATHS.V1}/tickets/${uuid}/logs?page=0&size=50`, { headers });
      }
      if ((!resp || !resp.ok) && ticketNumber) {
        resp = await fetch(`${API_PATHS.V1}/tickets/logs?ticketNumber=${encodeURIComponent(String(ticketNumber))}`, { headers });
      }
      if (!resp || !resp.ok) return;
      const data = await resp.json();
      const list = Array.isArray(data) ? data
        : (Array.isArray(data.content) ? data.content
        : (Array.isArray(data.data) ? data.data
        : (Array.isArray(data.items) ? data.items
        : (Array.isArray(data.results) ? data.results : []))));
      const createdEntry = list.find(x => (x.actionType || x.action_type) === 'CREATED');
      const occurredAt = createdEntry ? (createdEntry.occurredAt || createdEntry.occurred_at) : null;
      if (occurredAt) {
        setTicketCreatedMap(prev => ({ ...prev, [cacheKey]: occurredAt }));
      }
    } catch (e) {
      console.warn('Failed to fetch ticket created from history:', e?.message || e);
    }
  };

  // When workflow request loads, prefetch created timestamps for associated tickets
  useEffect(() => {
    try {
      const tickets = workflowRequest?.tickets || [];
      tickets.forEach(t => fetchTicketCreatedFromHistory(t));
    } catch {}
  }, [workflowRequest?.tickets]);

  // Debug: Expose formData and field info to window for DevTools inspection
  useEffect(() => {
    try {
      window.currentFormData = formData;
      const allFields = workflowRequest?.allFields || [];
      const docFields = allFields.filter((f) => isDocumentField(f) || f?.type === 'document');
      window.currentWorkflowFields = allFields;
      window.currentDocFieldNames = docFields.map((f) => f.name);
      console.debug('[Debug] formData/additionalInfo keys and document field names', {
        additionalInfoKeys: Object.keys(formData || {}),
        docFieldNames: window.currentDocFieldNames,
      });
    } catch (e) {
      console.warn('Debug exposure failed', e);
    }
  }, [formData, workflowRequest]);
  
  // Initialize form data when workflow request is loaded
  useEffect(() => {
    if (workflowRequest?.allFields) {
      const initialData = {};
      workflowRequest.allFields.forEach(field => {
        initialData[field.name] = field.value || '';
      });
      setFormData(initialData);
    }
  }, [workflowRequest]);
  
  // Separate useEffect for detailed workflow status logging
  useEffect(() => {
    if (workflowRequest) {
      console.log('Workflow request updated:', workflowRequest);
      console.log('Force update counter:', forceUpdate);
      
      if (workflowRequest.instance) {
        console.log('Workflow instance status:', workflowRequest.instance.status);
        console.log('Instance status type:', typeof workflowRequest.instance.status);
        console.log('Is workflow editable:', isWorkflowEditable(workflowRequest));
        console.log('Edit button should show:', isWorkflowEditable(workflowRequest));
      } else {
        console.log('No workflow instance found');
      }
    }
  }, [workflowRequest, forceUpdate]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  // Phase 3: Field categorization functions
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Get fields based on active category
  const getFilteredFields = () => {
    if (!workflowRequest?.allFields) return [];
    
    // Get fields based on the active tab
    let fields = [];
    
    if (activeTab === 'all') {
      fields = workflowRequest.allFields;
    } else {
      fields = workflowRequest.allFields.filter(field => {
        if (activeTab === 'personal' && (field.category === 'personal' || field.name.toLowerCase().includes('name') || field.name.toLowerCase().includes('email') || field.name.toLowerCase().includes('phone'))) {
          return true;
        }
        if (activeTab === 'business' && (field.category === 'business' || field.name.toLowerCase().includes('company') || field.name.toLowerCase().includes('organization') || field.name.toLowerCase().includes('business'))) {
          return true;
        }
        if (activeTab === 'other' && field.category !== 'personal' && field.category !== 'business') {
          return true;
        }
        return false;
      });
    }
    
    // Normalize all document fields
    const normalizedFields = fields.map(field => {
      if (isDocumentField(field)) {
        return normalizeDocumentField(field);
      }
      return field;
    });
    
    // Deduplicate document fields by name
    // Keep the one with more complete data (has documentUrl, fileName, etc.)
    const fieldMap = {};
    normalizedFields.forEach(field => {
      const fieldName = field.name;
      
      // Check if this is a document field
      const isDocument = field.type === 'document' || isDocumentField(field);
      
      if (isDocument) {
        // Helper function to score how complete a document field is
        const getDocumentCompleteness = (field) => {
          let score = 0;
          
          // Check if the field has hasFile=true
          if (field.value?.hasFile === true) score += 10;
          
          // Check if the field has a document URL
          if (field.value?.target?.value?.documentUrl) score += 5;
          
          // Check if the field has a file name
          if (field.value?.target?.value?.fileName) score += 3;
          
          // Check if the field has other metadata
          if (field.value?.target?.value?.contentType) score += 1;
          if (field.value?.target?.value?.fileSize) score += 1;
          
          return score;
        };
        
        // If we haven't seen this field before, add it
        if (!fieldMap[fieldName]) {
          fieldMap[fieldName] = field;
        } else {
          // If we have seen this field before, keep the one with more complete data
          const existingField = fieldMap[fieldName];
          const existingScore = getDocumentCompleteness(existingField);
          const newScore = getDocumentCompleteness(field);
          
          // If the new field has more complete data, replace the existing one
          if (newScore > existingScore) {
            console.log(`Replacing field ${fieldName} with more complete data. New score: ${newScore}, Old score: ${existingScore}`);
            fieldMap[fieldName] = field;
          }
        }
      } else {
        // For non-document fields, just keep the latest one
        fieldMap[fieldName] = field;
      }
    });
    
    // Convert the map back to an array
    return Object.values(fieldMap);
  };
  
  // Toggle edit mode
  const toggleEditMode = () => {
    if (editMode) {
      // Exiting edit mode without saving
      setEditMode(false);
      // Reset form data to original values
      const originalData = {};
      workflowRequest.allFields.forEach(field => {
        originalData[field.name] = field.value || '';
      });
      setFormData(originalData);
      setFormErrors({});
    } else {
      // Entering edit mode
      setEditMode(true);
    }
  };
  
  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when field is edited
    if (formErrors[fieldName]) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: undefined
      }));
    }
  };
  
  // Validate form fields
  const validateFields = () => {
    const errors = {};
    let isValid = true;
    
    workflowRequest.allFields.forEach(field => {
      if (field.required && (!formData[field.name] || formData[field.name].trim() === '')) {
        errors[field.name] = 'This field is required';
        isValid = false;
      } else if (field.type === 'email' && formData[field.name] && !/^\S+@\S+\.\S+$/.test(formData[field.name])) {
        errors[field.name] = 'Invalid email format';
        isValid = false;
      } else if (field.type === 'phone' && formData[field.name] && !/^[0-9\-\+\(\)\s]+$/.test(formData[field.name])) {
        errors[field.name] = 'Invalid phone number format';
        isValid = false;
      }
    });
    
    setFormErrors(errors);
    return isValid;
  };
  
  // Save changes
  const handleSaveChanges = async () => {
    if (!validateFields()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors before saving',
        severity: 'error'
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      const token = getToken();
      
      // Prepare updated data
      const updatedData = {
        ...workflowRequest.data,
        ...formData
      };
      
      // Send update request
      await axios.put(
        `${API_PATHS.API}/workflow-requests/${id}`,
        { data: updatedData },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Update local state
      setWorkflowRequest(prev => ({
        ...prev,
        data: updatedData,
        allFields: prev.allFields.map(field => ({
          ...field,
          value: formData[field.name] || ''
        }))
      }));
      
      setEditMode(false);
      setSnackbar({
        open: true,
        message: 'Changes saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      setSnackbar({
        open: true,
        message: `Error saving changes: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'primary';
      case 'FAILED':
        return 'error';
      case 'NOT_STARTED':
      case 'CREATED': // Add CREATED as equivalent to NOT_STARTED
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success.light';
      case 'IN_PROGRESS':
        return 'primary.light';
      case 'FAILED':
        return 'error.light';
      case 'NOT_STARTED':
      case 'CREATED': // Add CREATED as equivalent to NOT_STARTED
        return 'warning.light';
      default:
        return 'grey.300';
    }
  };
  
  // Helper function to check if a value is a file reference (legacy method)  
  // Now replaced by isDocumentField from documentUtils.js
  const isFileReference = (value) => {
    // Create a mock field object to use with isDocumentField
    const mockField = { value: value };
    return isDocumentField(mockField);
  };
  
  // Helper function to extract file name from a file reference string
  // Now replaced by extractFileName from documentUtils.js
  const extractFileNameLegacy = (fileReference) => {
    if (!fileReference || typeof fileReference !== 'string') return '';
    
    // Try to extract fileName
    const fileNameMatch = fileReference.match(/fileName=([^,]+)/i) || 
                         fileReference.match(/fileName":"([^"]+)/i) ||
                         fileReference.match(/filePath=([^,]+)/i) ||
                         fileReference.match(/documentUri=([^,]+)/i);
    
    if (fileNameMatch && fileNameMatch[1]) {
      // Clean up the file name
      let fileName = fileNameMatch[1];
      
      // Remove any path information and keep just the file name
      if (fileName.includes('/')) {
        fileName = fileName.split('/').pop();
      }
      
      // Remove any URL encoding
      try {
        fileName = decodeURIComponent(fileName);
      } catch (e) {
        // If decoding fails, use the original
      }
      
      return fileName;
    }
    
    // If no file name found, return empty string
    return '';
  };
  
  const extractDocumentType = (fileReference) => {
    const docTypeMatch = fileReference.match(/documentType=([^,]+)/i);
    if (docTypeMatch && docTypeMatch[1]) {
      return docTypeMatch[1] + ' File';
    }
    
    return 'Document';
  };
  
  // Helper function to extract document URL from a file reference string
  const extractDocumentUrl = (fileReference) => {
    if (!fileReference || typeof fileReference !== 'string') return null;
    
    // Try to extract documentUrl or documentUri
    const urlMatch = fileReference.match(/documentUrl=([^,}\]]+)/i) || 
                    fileReference.match(/documentUri=([^,}\]]+)/i) ||
                    fileReference.match(/documentUri":"([^"]+)/i);
    
    if (urlMatch && urlMatch[1]) {
      let url = urlMatch[1];
      
      // Remove any trailing special characters that might cause issues
      url = url.replace(/[\}\]\)\>]+$/, '');
      
      // If the URL doesn't start with http or /, assume it's a relative path
      if (!url.startsWith('http') && !url.startsWith('/')) {
        url = '/' + url;
      }
      
      // Log the extracted URL for debugging
      console.log('Extracted document URL:', url);
      
      return url;
    }
    
    // If we couldn't extract using regex, try a different approach
    // Look for common document URL patterns
    if (fileReference.includes('/documents/') || fileReference.includes('/documentUri=')) {
      const parts = fileReference.split(/documentUri=|documentUrl=/i);
      if (parts.length > 1) {
        let url = parts[1].split(/[,}\]\)\>"]/)[0];
        
        // If the URL doesn't start with http or /, assume it's a relative path
        if (!url.startsWith('http') && !url.startsWith('/')) {
          url = '/' + url;
        }
        
        // Log the extracted URL for debugging
        console.log('Extracted document URL (alternative method):', url);
        
        return url;
      }
    }
    
    return null;
  };
  
  // Function to view a document
  const viewDocument = (documentUrl, fileName) => {
    console.log('Viewing document:', documentUrl, fileName);
    
    if (!documentUrl) {
      console.error('No document URL provided');
      setSnackbar({
        open: true,
        message: 'Error: Document URL is missing',
        severity: 'error'
      });
      return;
    }
    
    // Extract employeeId and documentType if present in the URL
    let uid = '';
    let documentType = '';
    
    try {
      uid = extractUserId(documentUrl);
      documentType = extractDocumentType(documentUrl);
    } catch (e) {
      console.log('Could not extract uid and documentType from URL:', e);
    }
    
    console.log('Extracted uid:', uid, 'documentType:', documentType);
    
    // Set the current document and open the viewer
    setCurrentDocument({
      url: documentUrl,
      fileName: fileName || 'Document',
      uid: uid,
      documentType: documentType
    });
    
    setDocumentViewerOpen(true);
  };
  
  // Handler for viewing a file
  const handleViewFile = (fileReference) => {
    console.log('File reference:', fileReference);
    
    // Extract information from the file reference using our utilities
    const fileName = extractFileName(fileReference);
    let documentUrl = extractDocumentUrl(fileReference);
    const employeeId = extractUserId(fileReference);
    const documentType = extractDocumentType(fileReference);
    
    console.log('Extracted file info:', { fileName, documentUrl, employeeId, documentType });
    
    // If we couldn't extract a URL but have employee ID and document type, construct one
    if (!documentUrl && employeeId && documentType) {
      // Check if this is a dynamic service field (UUID pattern)
      const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(documentType);
      
      if (isDynamicServiceField) {
        console.log('Dynamic service field detected in WorkflowRequestDetails, cannot construct employee document URL for UUID documentType:', documentType);
        // Don't construct employee document URL for dynamic service fields
        // These should use file path URLs instead
      } else {
        documentUrl = `/v1/users/${employeeId}/documents/${documentType}/download`;
        console.log('Constructed URL from parts:', documentUrl);
      }
    }
    
    if (!documentUrl) {
      setSnackbar({
        open: true,
        message: 'Could not extract document URL from file reference',
        severity: 'error'
      });
      return;
    }
    
    // Use the new document viewer component
    viewDocument(documentUrl, fileName);
  };
  
  // Handler for closing the file viewer dialog (legacy - will be removed)
  const handleCloseFileViewer = () => {
    // Clean up any object URLs to prevent memory leaks
    if (fileViewerDialog.fileUrl && fileViewerDialog.fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fileViewerDialog.fileUrl);
    }
    
    setFileViewerDialog({
      open: false,
      fileName: '',
      fileUrl: '',
      fileType: '',
      loading: false,
      error: null
    });
  };
  
  // Handler for closing the document viewer
  const handleCloseDocumentViewer = () => {
    setDocumentViewerOpen(false);
  };
  
  // Helper function to check if a workflow is editable
  const isWorkflowEditable = (workflowRequest) => {
    // If there's no instance, it's editable
    if (!workflowRequest?.instance) return true;
    
    // If the status is CREATED or NOT_STARTED, it's editable
    const status = workflowRequest.instance.status;
    return status === 'CREATED' || status === 'NOT_STARTED';
  };

  const fetchWorkflowRequest = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      console.log('Fetching workflow request:', id);
      
      // Try both API and V1 endpoints to handle potential permission issues
      let response;
      try {
        response = await axios.get(
          `${API_PATHS.API}/workflow-requests/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      } catch (apiError) {
        console.log('Error with API endpoint, trying V1 endpoint:', apiError.message);
        // Try the V1 endpoint as fallback
        response = await axios.get(
          `${API_PATHS.V1}/workflow-requests/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }
      
      console.log('Fetching workflow done:', id);
      if (response.data) {
        // Log the raw API response
        console.log('API Response:', response.data);
        console.log('Instance object:', response.data.instance);
        console.log('Instance status:', response.data.instance?.status);
        console.log('Instance status type:', typeof response.data.instance?.status);
        console.log('Is workflow editable (from API):', isWorkflowEditable(response.data));
        console.log('JSON.stringify of instance:', JSON.stringify(response.data.instance));
        
        // If the response has the expected structure from the API you shared
        if (response.data.instance) {
          console.log('Found instance directly in response');
          // No need to parse workflow definition if we already have the data structure
          setWorkflowRequest(response.data);
          // Force a re-render to ensure the edit button visibility is updated
          setForceUpdate(prev => prev + 1);
          
          // Extract fields from steps if available
          if (response.data.steps && response.data.steps.length > 0) {
            console.log('Extracting fields from steps in direct response');
            const allFields = [];
            
            response.data.steps.forEach(step => {
              if (step.fieldValues) {
                // Extract fields from fieldValues.fields
                if (step.fieldValues.fields) {
                  Object.entries(step.fieldValues.fields).forEach(([fieldName, fieldData]) => {
                    // Case 1: Direct document structure (hasFile and target at the root level)
                    const isDirectDocumentField = 
                      fieldData.hasFile !== undefined && 
                      fieldData.target !== undefined;
                      
                    // Case 2: Nested document structure (inside value property)
                    const isNestedDocumentField = 
                      typeof fieldData.value === 'object' && 
                      fieldData.value !== null && 
                      fieldData.value.hasFile !== undefined && 
                      fieldData.value.target !== undefined;
                    
                    if (isDirectDocumentField) {
                      // This is a direct document field structure
                      console.log('Found direct document field structure in fields:', fieldName);
                      allFields.push({
                        name: fieldName,
                        label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                        type: 'document',
                        value: fieldData, // Use the field data directly
                        required: false
                      });
                    } else if (isNestedDocumentField) {
                      // This is a nested document field structure
                      console.log('Found nested document field structure in fields:', fieldName);
                      allFields.push({
                        name: fieldName,
                        label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                        type: 'document',
                        value: fieldData.value,
                        required: false
                      });
                    } else {
                      // Regular field or document field needing normalization
                      const field = {
                        name: fieldName,
                        label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                        type: fieldData.type?.toLowerCase() || 'text',
                        value: fieldData.value || '',
                        required: false
                      };
                      
                      // Check if this is a document field and normalize it
                      if (isDocumentField(field)) {
                        console.log('Normalizing document field from fields:', fieldName, field);
                        allFields.push(normalizeDocumentField(field));
                      } else {
                        allFields.push(field);
                      }
                    }
                  });
                }
                
                // Extract fields from fieldValues.staticFields
                if (step.fieldValues.staticFields) {
                  Object.entries(step.fieldValues.staticFields).forEach(([fieldName, fieldData]) => {
                    if (typeof fieldData === 'object' && fieldData !== null) {
                      // Case 1: Direct document structure (hasFile and target at the root level)
                      const isDirectDocumentField = 
                        fieldData.hasFile !== undefined && 
                        fieldData.target !== undefined;
                        
                      // Case 2: Nested document structure (inside value property)
                      const isNestedDocumentField = 
                        typeof fieldData.value === 'object' && 
                        fieldData.value !== null && 
                        fieldData.value.hasFile !== undefined && 
                        fieldData.value.target !== undefined;
                      
                      if (isDirectDocumentField) {
                        // This is a direct document field structure
                        console.log('Found direct document field structure:', fieldName);
                        allFields.push({
                          name: fieldName,
                          label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                          type: 'document',
                          value: fieldData, // Use the field data directly
                          required: false
                        });
                      } else if (isNestedDocumentField) {
                        // This is a nested document field structure
                        console.log('Found nested document field structure:', fieldName);
                        allFields.push({
                          name: fieldName,
                          label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                          type: 'document',
                          value: fieldData.value,
                          required: false
                        });
                      } else {
                        // Regular field or document field needing normalization
                        const field = {
                          name: fieldName,
                          label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                          type: fieldData.type?.toLowerCase() || 'text',
                          value: fieldData.value || '',
                          required: false
                        };
                        
                        // Check if this is a document field and normalize it
                        if (isDocumentField(field)) {
                          console.log('Normalizing document field:', fieldName, field);
                          allFields.push(normalizeDocumentField(field));
                        } else {
                          allFields.push(field);
                        }
                      }
                    }
                  });
                }
              }
            });
            
            // Add the extracted fields to the workflow request
            response.data.allFields = allFields;
            setWorkflowRequest(response.data);
            setLoading(false);
            return; // Skip the rest of the processing
          }
        }
        
        // If we didn't return above, continue with the original flow
        console.log('Fetching workflow 1:', id);
        if (response.data.workflowInfo?.definition) {
          try {
            console.log('Fetching workflow 2:', id);
            const definition = JSON.parse(response.data.workflowInfo.definition);
            response.data.steps = definition.steps;
            
            // Extract all fields from the definition
            let allFields = definition.fields || [];
            
            // Extract field values from the request data
            if (response.data.data) {
              allFields = allFields.map(field => ({
                ...field,
                value: response.data.data[field.name] || ''
              }));
            }
            
            // Extract fields from steps if available (new format)
            if (response.data.steps && response.data.steps.length > 0) {
              console.log('Extracting fields from steps');
              response.data.steps.forEach(step => {
                if (step.fieldValues) {
                  // Extract fields from fieldValues.fields
                  if (step.fieldValues.fields) {
                    Object.entries(step.fieldValues.fields).forEach(([fieldName, fieldData]) => {
                      allFields.push({
                        name: fieldName,
                        label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                        type: fieldData.type?.toLowerCase() || 'text',
                        value: fieldData.value || '',
                        required: false
                      });
                    });
                  }
                  
                  // Extract fields from fieldValues.staticFields
                  if (step.fieldValues.staticFields) {
                    Object.entries(step.fieldValues.staticFields).forEach(([fieldName, fieldData]) => {
                      if (typeof fieldData === 'object' && fieldData !== null) {
                        allFields.push({
                          name: fieldName,
                          label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1'),
                          type: fieldData.type?.toLowerCase() || 'text',
                          value: fieldData.value || '',
                          required: false
                        });
                      }
                    });
                  }
                }
              });
            }
            
            response.data.allFields = allFields;
          } catch (parseError) {
            console.error('Error parsing workflow definition:', parseError);
          }
        }
        
        // Fetch documents if available
        if (response.data.id) {
          try {
            // Try multiple approaches to get the employee UID
            const employeeUid = response.data.data?.uid || 
                              response.data.employeeUid || 
                              response.data.data?.employeeUid || 
                              response.data.data?.employeeId;
            
            if (employeeUid) {
              console.log('Fetching documents for employee:', employeeUid);
              
              // Try multiple document endpoints to handle potential permission issues
              let documentsResponse;
              try {
                // First try the user documents endpoint
                documentsResponse = await axios.get(
                  `${API_PATHS.V1}/users/${employeeUid}/documents`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`
                    }
                  }
                );
              } catch (userDocError) {
                console.log('Error fetching user documents, trying employee documents:', userDocError.message);
                // Try employee documents endpoint as fallback
                try {
                  documentsResponse = await axios.get(
                    `${API_PATHS.V1}/employees/${employeeUid}/documents`,
                    {
                      headers: {
                        Authorization: `Bearer ${token}`
                      }
                    }
                  );
                } catch (empDocError) {
                  console.log('Error fetching employee documents, trying workflow documents:', empDocError.message);
                  // Try workflow request documents as last resort
                  documentsResponse = await axios.get(
                    `${API_PATHS.V1}/workflow-requests/${id}/documents`,
                    {
                      headers: {
                        Authorization: `Bearer ${token}`
                      }
                    }
                  );
                }
              }
              
              if (documentsResponse?.data) {
                // Format documents for display (reserved for future use)
                /* const formattedDocuments = documentsResponse.data.map(doc => {
                  // Determine the correct document URL based on available data
                  let documentUrl;
                  if (doc.documentType) {
                    // Check if this is a dynamic service field (UUID pattern)
                    const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(doc.documentType);
                    
                    if (isDynamicServiceField) {
                      console.log('Dynamic service field detected in WorkflowRequestDetails document formatting, using file path instead of employee document API for UUID documentType:', doc.documentType);
                      // For dynamic service fields, use file path if available
                      documentUrl = doc.filePath ? `${API_PATHS.V1}/files/download?filePath=${encodeURIComponent(doc.filePath)}` : null;
                    } else {
                      documentUrl = `${API_PATHS.V1}/users/${employeeUid}/documents/${doc.documentType}/download`;
                    }
                  } else if (doc.id) {
                    documentUrl = `${API_PATHS.V1}/documents/${doc.id}/download`;
                  } else {
                    documentUrl = `${API_PATHS.V1}/workflow-requests/${id}/documents/${doc.name || 'document'}/download`;
                  }
                  
                  return {
                    id: doc.id || `doc-${Math.random().toString(36).substr(2, 9)}`,
                    name: doc.documentType || doc.name || 'Document',
                    type: doc.documentType,
                    contentType: doc.contentType || 'application/octet-stream',
                    uploadDate: doc.createDateTime || new Date().toISOString(),
                    url: documentUrl,
                    employeeUid: employeeUid
                  };
                }); */
                // Note: formattedDocuments intentionally not used here. Keeping this block for reference without affecting runtime.
              }
            }
          } catch (docError) {
            console.error('Error fetching workflow documents:', docError);
            setSnackbar({
              open: true,
              message: 'Error loading documents. Some features may be limited.',
              severity: 'warning'
            });
            // Don't fail the entire request if documents can't be fetched
          }
        }
        
        // Log the successful data retrieval
        console.log('Successfully retrieved workflow request data:', response.data);
        
        setWorkflowRequest(response.data);
      }
    } catch (error) {
      console.error('Error fetching workflow request:', error);
      
      // Provide a more user-friendly error message
      if (error.response) {
        if (error.response.status === 403) {
          setError('Access denied: You do not have permission to view this workflow request');
        } else if (error.response.status === 404) {
          setError('Workflow request not found. It may have been deleted or you have entered an invalid ID.');
        } else {
          setError(`Server error (${error.response.status}): ${error.response.data || error.message}`);
        }
      } else if (error.request) {
        setError('Network error: The server is not responding. Please check your connection and try again.');
      } else {
        setError('Error fetching workflow request: ' + error.message);
      }
      
      setSnackbar({
        open: true,
        message: 'Failed to load workflow request details',
        severity: 'error'
      });
      
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (definition, services) => {
    try {
      if (!definition || !services) return 0;
      const totalSteps = definition.length;
      const completedSteps = services.filter(s => s.status === 'COMPLETED').length;
      return (completedSteps / totalSteps) * 100;
    } catch (error) {
      console.error('Error calculating progress:', error);
      return 0;
    }
  };

  const validateWorkflowForStart = () => {
    if (!workflowRequest?.instance?.id) {
      throw new Error('No workflow instance found');
    }
    if (!workflowRequest.workflowInfo?.id) {
      throw new Error('No workflow ID found');
    }
    if (!workflowRequest.workflowInfo?.definition) {
      throw new Error('No workflow definition found');
    }

    let definition;
    try {
      definition = JSON.parse(workflowRequest.workflowInfo.definition);
    } catch (error) {
      throw new Error('Invalid workflow definition format');
    }

    if (!definition.steps || definition.steps.length === 0) {
      throw new Error('No steps found in workflow definition');
    }

    // Validate each step has required data
    definition.steps.forEach((step, index) => {
      if (!step.id) {
        throw new Error(`Step ${index + 1} is missing an ID`);
      }
      if (!step.serviceId) {
        throw new Error(`Step ${step.id} is missing a service ID`);
      }
    });

    return definition;
  };

  const handleStartWorkflow = async () => {
    console.log('Start workflow button clicked');
    try {
      setLoading(true);
      
      // Log the full workflow request for debugging
      console.log('Full workflow request:', workflowRequest);
      
      // Validate required fields
      if (!workflowRequest.instance?.id) {
        throw new Error('Missing instance ID');
      }
      
      // Use the centralized workflow service to start the workflow instance
      await workflowService.startWorkflowInstance(workflowRequest.instance.id);
      
      // Refresh the workflow request data to get updated status
      await fetchWorkflowRequest();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Workflow Started',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error starting workflow:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Error starting workflow',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle skip step button click - open confirmation dialog
  const handleSkipStepClick = (stepId, stepName) => {
    setSkipStepDialog({
      open: true,
      stepId,
      stepName
    });
  };

  // Close skip step confirmation dialog
  const handleCloseSkipDialog = () => {
    setSkipStepDialog({
      open: false,
      stepId: null,
      stepName: ''
    });
  };

  // Confirm and execute skip step
  const handleConfirmSkipStep = async () => {
    const { stepId } = skipStepDialog;
    if (!stepId || !id) return;

    try {
      setLoading(true);
      
      // Call the skip step API
      const token = getToken();
      await axios.patch(
        `${API_PATHS.API}/workflow-requests/${id}/steps/${stepId}/skip`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Close the dialog
      handleCloseSkipDialog();
      
      // Refresh the workflow request data to get updated step status
      await fetchWorkflowRequest();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Step skipped successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error skipping step:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Error skipping step',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Debug info before render
  console.log('Rendering WorkflowRequestDetails with state:', {
    workflowRequest,
    editMode,
    loading,
    error,
    formData,
    formErrors,
    activeTab,
    'workflowRequest.instance?.status': workflowRequest?.instance?.status,
    'Is workflow editable': workflowRequest ? isWorkflowEditable(workflowRequest) : false,
    'Edit button should show': workflowRequest ? isWorkflowEditable(workflowRequest) : false
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Page title */}
      <Typography variant="h4" component="h1" gutterBottom>
        Workflow Request Details
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
          </Box>
        </Grid>

        {loading ? (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          </Grid>
        ) : error ? (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        ) : workflowRequest ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">
                    Workflow Request Details
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleStartWorkflow}
                      disabled={!!workflowRequest?.instance && workflowRequest.instance.status !== 'CREATED' && workflowRequest.instance.status !== 'NOT_STARTED'}
                    >
                      {(!workflowRequest?.instance || workflowRequest.instance.status === 'CREATED' || workflowRequest.instance.status === 'NOT_STARTED')
                        ? 'Start Workflow'
                        : 'Workflow Started'}
                    </Button>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Workflow Information
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          {workflowRequest.instance && (
                            <Box sx={{ mb: 2 }}>
                              <Chip
                                label={`Status: ${workflowRequest.instance.status}`}
                                color={getStatusColor(workflowRequest.instance.status)}
                                sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 0.5 }}
                              />
                              {isWorkflowEditable(workflowRequest) && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                  This workflow is in {workflowRequest.instance.status} status and can be edited
                                </Typography>
                              )}
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BusinessIcon sx={{ mr: 1 }} />
                            <Typography variant="body1">
                              <strong>Organization:</strong> {workflowRequest.organization?.name || workflowRequest.organizationName || workflowRequest.customerOrganisation?.name || 'Not specified'}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary" paragraph>
                            Request ID: {workflowRequest.id}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" paragraph>
                            Organization Request #: {workflowRequest.orgSequenceNumber}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" paragraph>
                            Title: {workflowRequest.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" paragraph>
                            Workflow Type: {workflowRequest.workflowTargetType === 'ORGANIZATION' ? 'Organization' : 'Employee'}
                          </Typography>
                          {/* Duplicate status based on completionStatus removed */}
                          {(() => {
                            const formatSafe = (v) => {
                              if (!v) return 'N/A';
                              try {
                                const d = new Date(v);
                                return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
                              } catch { return 'N/A'; }
                            };
                            const createdRaw = workflowRequest.instance?.createdAt || workflowRequest.createDateTime || workflowRequest.createdAt;
                            const updatedRaw = workflowRequest.instance?.updatedAt || workflowRequest.updateDateTime || workflowRequest.updatedAt;
                            return (
                              <>
                                <Typography variant="body2" color="textSecondary" paragraph>
                                  Created: {formatSafe(createdRaw)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" paragraph>
                                  Last Updated: {formatSafe(updatedRaw)}
                                </Typography>
                              </>
                            );
                          })()}
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Workflow Definition
                          </Typography>
                          {/* Name/Description/Version moved to Workflow Definition panel */}
                          {/* Type/Variant removed per requirement */}
                        </Grid>
                      </Grid>
                      
                      {workflowRequest.instance && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            {/* Workflow Definition panel */}
                            <Typography variant="subtitle2" gutterBottom>
                              Workflow Definition
                            </Typography>
                            {(() => {
                              const wfDef = workflowRequest.workflow || workflowRequest.definition || workflowRequest.workflowDefinition || workflowRequest.workflowInfo || {};
                              const name = wfDef.name || workflowRequest.workflowName || 'N/A';
                              const description = wfDef.description || workflowRequest.description || 'N/A';
                              const version = wfDef.version || wfDef.versionNumber || workflowRequest.workflowVersion || workflowRequest.version || 'N/A';
                              return (
                                <>
                                  <Typography variant="body2" color="textSecondary" paragraph>
                                    Name: {name}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary" paragraph>
                                    Description: {description}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary" paragraph>
                                    Version: {version}
                                  </Typography>
                                </>
                              );
                            })()}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Workflow Instance
                            </Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>
                              Instance ID: {workflowRequest.instance.id}
                            </Typography>
                            {/* Duplicate instance status removed (keep top chip only) */}
                            <Typography variant="body2" color="textSecondary" paragraph>
                              Created: {workflowRequest.instance.createdAt ? new Date(workflowRequest.instance.createdAt).toLocaleString() : 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                      
                      {/* Total Charges Section */}
                      {(workflowRequest.totalServiceCharge !== undefined || 
                        workflowRequest.totalTax !== undefined || 
                        workflowRequest.totalGovtCharge !== undefined || 
                        workflowRequest.totalCost !== undefined) && (
                        <Box sx={{  mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Grid container spacing={2}>
                            {workflowRequest.totalServiceCharge !== undefined && (
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" display="block" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                                  Service Charge
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                  AED {Number(workflowRequest.totalServiceCharge || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                            {workflowRequest.totalTax !== undefined && (
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" display="block" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                                  Tax
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                  AED {Number(workflowRequest.totalTax || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                            {workflowRequest.totalGovtCharge !== undefined && (
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" display="block" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                                  Govt Charge
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                  AED {Number(workflowRequest.totalGovtCharge || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                            {workflowRequest.totalCost !== undefined && (
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" display="block" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                                  Grand Total
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                                  AED {Number(workflowRequest.totalCost || 0).toFixed(2)}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )}
                      
                      {workflowRequest.tickets && workflowRequest.tickets.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Active Tickets
                          </Typography>
                          {workflowRequest.tickets.map((ticket, index) => (
                            <Box key={ticket.id} sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="textSecondary" component="span">
                                  {ticket.serviceName}
                                </Typography>
                                <Chip 
                                  label={ticket.status} 
                                  size="small"
                                  color={getStatusColor(ticket.status)}
                                  sx={{ 
                                    bgcolor: getStatusBgColor(ticket.status)
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" display="block" color="textSecondary">
                                Ticket #{ticket.ticketNumber} - Created: {new Date(ticket.createDateTime).toLocaleString()}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
                
                {/* Field Display Section - Phase 3 Enhanced UI */}
                {workflowRequest?.allFields?.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    {/* Grouped Employee Documents (Read-only) */}
                    {FEATURE_WORKFLOW_DOC_GROUPS && FEATURE_WORKFLOW_DOC_GROUPS_UI && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Employee Documents (Grouped)
                        </Typography>
                        {groupedDocsLoading && (
                          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Loading grouped documents...</Typography>
                          </Box>
                        )}
                        {groupedDocsError && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            {groupedDocsError}
                          </Alert>
                        )}
                        {groupedDocs && (
                          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Grid container spacing={2}>
                              {Object.entries(groupedDocs || {}).map(([docType, groups]) => (
                                <Grid item xs={12} key={docType}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      {docType}
                                    </Typography>
                                  </Box>
                                  <Grid container spacing={1}>
                                    {(Array.isArray(groups) ? groups : []).map((group, idx) => (
                                      <Grid item xs={12} md={6} key={`${docType}-${group?.common?.documentGroupId || idx}`}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              Group: {group?.common?.documentGroupId || 'N/A'} {group?.common?.isCurrent ? '(Current)' : ''}
                                            </Typography>
                                            <Chip size="small" label={`Docs: ${(group?.documents || []).length}`} />
                                          </Box>
                                          <Box sx={{ mt: 1 }}>
                                            {(group?.documents || []).map((d) => (
                                              <Box key={d.id || d.filePath || d.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                {FEATURE_WORKFLOW_DOC_SELECTIONS && (
                                                  <Checkbox
                                                    size="small"
                                                    checked={isDocSelected(docType, group?.common?.documentGroupId, d.id || d.filePath || d.name)}
                                                    onChange={() => toggleDocSelection(docType, group?.common?.documentGroupId, d)}
                                                  />
                                                )}
                                                <InsertDriveFileIcon fontSize="small" />
                                                <Typography variant="body2" noWrap>
                                                  {d.fileName || d.name || d.id} v{d.version || 1} {d.isPrimary ? '(Primary)' : ''}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Paper>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        )}
                        {/* Selection preview */}
                        {FEATURE_WORKFLOW_DOC_SELECTIONS && documentSelections && documentSelections.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Selected Documents (Live Preview)
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {JSON.stringify(documentSelections, null, 2)}
                              </pre>
                            </Paper>
                          </Box>
                        )}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Form Fields
                      </Typography>
                      <Box>
                        {/* Removed debug status caption */}
                        
                        {/* Edit Fields / Save Changes removed: the backend has no endpoint to update a
                            request's field values after creation, so the flow was non-functional. */}
                      </Box>
                    </Box>
                    
                    <Paper sx={{ p: 2 }}>
                      {/* Field Category Tabs */}
                      <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ 
                          mb: 3, 
                          borderBottom: 1, 
                          borderColor: 'divider',
                          '& .MuiTab-root': {
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            py: 1.5,
                            px: 2
                          },
                          '& .Mui-selected': {
                            color: 'primary.main',
                            bgcolor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                        TabIndicatorProps={{
                          style: {
                            height: '3px'
                          }
                        }}
                      >
                        <Tab 
                          icon={<AssignmentIcon />} 
                          iconPosition="start" 
                          label="Steps" 
                          value="steps" 
                        />
                        <Tab 
                          icon={<ListAltIcon />} 
                          iconPosition="start" 
                          label="All Fields" 
                          value="all" 
                        />
                        <Tab 
                          icon={<PersonIcon />} 
                          iconPosition="start" 
                          label="Personal Info" 
                          value="personal" 
                        />
                        <Tab 
                          icon={<BusinessIcon />} 
                          iconPosition="start" 
                          label="Business Info" 
                          value="business" 
                        />
                        <Tab 
                          icon={<CategoryIcon />} 
                          iconPosition="start" 
                          label="Other Fields" 
                          value="other" 
                        />
                      </Tabs>
                      <Divider sx={{ borderColor: 'divider', mb: 2 }} />
                      
                      {/* Steps Tab Content */}
                      {activeTab === 'steps' && (
                          <>
                          <Divider sx={{ borderColor: 'divider', mb: 2 }} />
                          <Box mt={2}>
                            {Array.isArray(workflowRequest?.steps) && workflowRequest.steps.length > 0 && (
                              workflowRequest.steps.map((step, index) => {
                                const isLastStep = index === workflowRequest.steps.length - 1;
                                // Find associated ticket for this step from either ticketInfo or tickets array
                                const associatedTicket = step.ticketInfo || workflowRequest.tickets?.find(ticket => ticket.workflowStepId === step.id);
                                const status = associatedTicket ? associatedTicket.status : step.completionStatus;
                                const stepFields = step.fields || [];
                                
                                // Format dates if available (prefer ticket history CREATED if associated ticket exists)
                                let createdDate = 'N/A';
                                if (associatedTicket) {
                                  const cacheKey = (associatedTicket.id && isUuid(associatedTicket.id)) ? associatedTicket.id
                                    : (associatedTicket.ticketNumber ? `num:${associatedTicket.ticketNumber}` : null);
                                  const createdIso = cacheKey ? ticketCreatedMap[cacheKey] : null;
                                  if (createdIso) {
                                    try { createdDate = new Date(createdIso).toLocaleString(); } catch { createdDate = createdIso; }
                                  } else if (associatedTicket.createDateTime) {
                                    try { createdDate = new Date(associatedTicket.createDateTime).toLocaleString(); } catch { createdDate = associatedTicket.createDateTime; }
                                  } else if (step.createdAt) {
                                    try { createdDate = new Date(step.createdAt).toLocaleString(); } catch { createdDate = step.createdAt; }
                                  }
                                }

                                // Compute additional meta used in rendering
                                const completedDate = step.completedAt ? (() => { try { return new Date(step.completedAt).toLocaleString(); } catch { return step.completedAt; } })() : 'N/A';
                                const updatedDate = step.updatedAt ? (() => { try { return new Date(step.updatedAt).toLocaleString(); } catch { return step.updatedAt; } })() : 'N/A';
                                const isRunning = ['RUNNING', 'IN_PROGRESS'].includes(String(status || '').toUpperCase());

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  mb: isLastStep ? 0 : 2
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mr: 2
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: getStatusColor(status),
                      color: 'common.white',
                      width: 36,
                      height: 36
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  {!isLastStep && (
                    <Box
                      sx={{
                        height: 30,
                        width: 2,
                        bgcolor: 'grey.300',
                        my: 0.5
                      }}
                    />
                  )}
                </Box>
                <Paper
                  sx={{
                    p: 2,
                    flex: 1,
                    borderLeft: `4px solid ${getStatusBgColor(status)}`,
                    borderRadius: 1,
                    position: 'relative'
                  }}
                  elevation={1}
                >
                  {/* Step Header with Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {step.name || `Step ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {step.description || 'No description available'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={status}
                        size="small"
                        color={getStatusColor(status)}
                        sx={{ bgcolor: getStatusBgColor(status) }}
                      />
                      {status === 'NOT_STARTED' && !step.skip && (
                        <Button
                          variant="contained"
                          size="small"
                          color="warning"
                          onClick={() => handleSkipStepClick(step.id, step.name || `Step ${index + 1}`)}
                          sx={{ 
                            minWidth: '80px',
                            fontWeight: 600
                          }}
                        >
                          Skip
                        </Button>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Step Details Section */}
                  <Box sx={{ mt: 1, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {/* Step ID */}
                    <Box sx={{ minWidth: '200px' }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Step ID
                      </Typography>
                      <Typography variant="body2">
                        {step.id || 'N/A'}
                      </Typography>
                    </Box>
                    
                    {/* Step Type */}
                    <Box sx={{ minWidth: '150px' }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Type
                      </Typography>
                      <Typography variant="body2">
                        {step.type || 'Standard'}
                      </Typography>
                    </Box>
                    
                    {/* Created Date */}
                    <Box sx={{ minWidth: '200px' }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {createdDate}
                      </Typography>
                    </Box>
                    
                    {/* Updated Date if available */}
                    {step.updatedAt && (
                      <Box sx={{ minWidth: '200px' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Last Updated
                        </Typography>
                        <Typography variant="body2">
                          {updatedDate}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Completed Date if available */}
                    {status === 'COMPLETED' && (
                      <Box sx={{ minWidth: '200px' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Completed
                        </Typography>
                        <Typography variant="body2">
                          {completedDate}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Step Charges Section */}
                  {(step.totalServiceCharge ?? step.totalTax ?? step.totalGovtCharge ?? step.totalCost) !== undefined && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: step.skip ? 'grey.100' : 'grey.50',
                        borderRadius: 1,
                        opacity: step.skip ? 0.8 : 1,
                      }}
                    >
                      <Grid container spacing={2}>
                        {[
                          { label: 'Service Charge', value: step.totalServiceCharge },
                          { label: 'Tax', value: step.totalTax },
                          { label: 'Govt Charge', value: step.totalGovtCharge },
                          { label: 'Total Cost', value: step.totalCost },
                        ].map(
                          (item) =>
                            item.value !== undefined && (
                              <Grid item xs={6} sm={3} key={item.label}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {item.label}
                                </Typography>

                                {/* Amount display */}
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {step.skip ? (
                                    <>
                                      <span
                                        style={{
                                          textDecoration: 'line-through',
                                          color: 'gray',
                                          marginRight: 6,
                                        }}
                                      >
                                        AED {Number(item.value || 0).toFixed(2)}
                                      </span>
                                      <span>AED 0.00</span>
                                    </>
                                  ) : (
                                    <>AED {Number(item.value || 0).toFixed(2)}</>
                                  )}
                                </Typography>
                              </Grid>
                            )
                        )}
                      </Grid>
                    </Box>
                  )}
                  {/* Running Indicator */}
                  {isRunning && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'primary.main' }}>
                      <CircularProgress size={16} thickness={5} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="primary">
                        Step is currently running
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Step Fields Section */}
                  {stepFields.length > 0 && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Step Fields
                      </Typography>
                      <Grid container spacing={2}>
                        {stepFields.map((field, fieldIndex) => (
                          <Grid item xs={12} sm={6} key={fieldIndex}>
                            <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {field.label || field.name}
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {field.value || 'Not set'}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  
                  {/* Ticket Information Section */}
                  {associatedTicket && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Ticket Information
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Ticket number as hyperlink */}
                        <Typography
                          variant="body2"
                          color="secondary"
                          component="a"
                          sx={{ textDecoration: 'none', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.preventDefault();
                            const targetId = associatedTicket.id || associatedTicket.uid || associatedTicket.ticketNumber;
                            if (!targetId) return;
                            navigate(`/tickets/${targetId}`);
                          }}
                        >
                          #{associatedTicket.ticketNumber}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="span">
                          {associatedTicket.title || 'No title'}
                        </Typography>
                      </Box>
                      {associatedTicket.assignedTo && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                          {associatedTicket.assignedTo === 'THAKLIS' || associatedTicket.assignedTo === 'Organization' ? (
                            <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          ) : (
                            <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          )}
                          <Typography variant="body2" color="textSecondary">
                            Assigned to: {associatedTicket.assignedTo}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Total Cost */}
                      {associatedTicket.totalCost !== undefined && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                          <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            Total Cost: {typeof associatedTicket.totalCost === 'number' ? 
                              `$${associatedTicket.totalCost.toFixed(2)}` : 
                              associatedTicket.totalCost}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
            );
          })
                            )}
                            {(!Array.isArray(workflowRequest?.steps) || workflowRequest.steps.length === 0) && (
                              <Paper sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" color="textSecondary">
                                  No workflow steps found.
                                </Typography>
                              </Paper>
                            )}
                          </Box>
                          </>
                      )}
                      
                      {/* Field Grid with Filtered Fields */}
                      {activeTab !== 'steps' && (
                        <Grid container spacing={2}>
                          {getFilteredFields().map((field) => (
                          <Grid item xs={12} sm={6} md={4} key={field.name}>
                            <Box sx={{ mb: 2 }}>
                              {editMode ? (
                                <FormControl 
                                  fullWidth 
                                  error={!!formErrors[field.name]}
                                  variant="outlined"
                                  size="small"
                                  sx={{ mb: 1 }}
                                >
                                  <Typography variant="subtitle2" gutterBottom>
                                    {field.label || field.name}
                                    {field.required && <span style={{ color: 'red' }}> *</span>}
                                  </Typography>
                                  
                                  {isDocumentField(field) ? (
                                    (() => {
                                      const docProps = createDocumentFieldProps(field);
                                      console.debug('[EditMode] Document field render decision', {
                                        name: field.name,
                                        hasFile: docProps?.hasFile,
                                        hasFileStrict: docProps?.hasFileStrict,
                                        refsCount: Array.isArray(docProps?.refs) ? docProps.refs.length : 0,
                                        fileName: docProps?.fileName,
                                        documentUrl: docProps?.documentUrl,
                                        rawFieldValue: field?.value,
                                        docProps
                                      });
                                      return docProps?.hasFile ? (
                                        <DocumentFieldRenderer field={field} label={field.label || field.name} />
                                      ) : (
                                        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                          No document provided
                                        </Typography>
                                      );
                                    })()
                                  ) : field.type === 'select' ? (
                                    <Select
                                      labelId={`label-${field.name}`}
                                      value={formData[field.name] || ''}
                                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                      placeholder={`Select ${field.label || field.name}`}
                                      displayEmpty
                                    >
                                      {field.options?.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          {option.label}
                                        </MenuItem>
                                      )) || (
                                        <MenuItem value="">No options available</MenuItem>
                                      )}
                                    </Select>
                                  ) : field.type === 'checkbox' ? (
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={!!formData[field.name]}
                                          onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                                        />
                                      }
                                      label={field.label || field.name}
                                    />
                                  ) : field.type === 'textarea' ? (
                                    <TextField
                                      multiline
                                      rows={3}
                                      value={formData[field.name] || ''}
                                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                      placeholder={`Enter ${field.label || field.name}`}
                                      error={!!formErrors[field.name]}
                                      fullWidth
                                    />
                                  ) : (
                                    <TextField
                                      type={field.type === 'number' ? 'number' : 'text'}
                                      value={formData[field.name] || ''}
                                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                      placeholder={`Enter ${field.label || field.name}`}
                                      error={!!formErrors[field.name]}
                                      fullWidth
                                    />
                                  )}
                                  
                                  {formErrors[field.name] && (
                                    <FormHelperText>{formErrors[field.name]}</FormHelperText>
                                  )}
                                </FormControl>
                              ) : (
                                <>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {field.label || field.name}
                                    {field.required && (
                                      <span style={{ color: 'red' }}> *</span>
                                    )}
                                  </Typography>
                                  {isDocumentField(field) || field.type === 'document' ? (
                                    (() => {
                                      const docProps = createDocumentFieldProps(field);
                                      console.debug('[ReadOnly] Document field render decision', {
                                        name: field.name,
                                        hasFile: docProps?.hasFile,
                                        hasFileStrict: docProps?.hasFileStrict,
                                        refsCount: Array.isArray(docProps?.refs) ? docProps.refs.length : 0,
                                        fileName: docProps?.fileName,
                                        documentUrl: docProps?.documentUrl,
                                        rawFieldValue: field?.value,
                                        docProps
                                      });
                                      return docProps?.hasFile ? (
                                        <DocumentFieldRenderer field={field} label={field.label || field.name} />
                                      ) : (
                                        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                          No document provided
                                        </Typography>
                                      );
                                    })()
                                  ) : typeof field.value === 'object' && field.value !== null ? (
                                    // Check if this might be a document field that wasn't properly detected
                                    field.value.hasFile !== undefined && field.value.target !== undefined ? (
                                      (() => {
                                        const docProps = createDocumentFieldProps(field);
                                        console.debug('[ReadOnly-Normalized] Document field render decision', {
                                          name: field.name,
                                          hasFile: docProps?.hasFile,
                                          hasFileStrict: docProps?.hasFileStrict,
                                          refsCount: Array.isArray(docProps?.refs) ? docProps.refs.length : 0,
                                          fileName: docProps?.fileName,
                                          documentUrl: docProps?.documentUrl,
                                          rawFieldValue: field?.value,
                                          docProps
                                        });
                                        return docProps?.hasFile ? (
                                          <DocumentFieldRenderer field={field} label={field.label || field.name} />
                                        ) : (
                                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                            No document provided
                                          </Typography>
                                        );
                                      })()
                                    ) : (
                                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        {field.value.displayValue || JSON.stringify(field.value)}
                                      </Typography>
                                    )
                                  ) : (
                                    <Typography variant="body2" sx={{ color: field.value ? 'text.primary' : 'text.disabled', fontWeight: field.value ? 500 : 400 }}>
                                      {field.value || 'Not provided'}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          </Grid>
                        ))}
                        
                        {getFilteredFields().length === 0 && (
                          <Grid item xs={12}>
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                              <Typography variant="body1" color="textSecondary">
                                No fields found in this category.
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      )}
                    </Paper>
                  </Box>
                )}
                
                {/* Document Display Section */}
                {workflowRequest?.documents?.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Documents
                    </Typography>
                    <Paper sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        {workflowRequest.documents.map((doc) => (
                          <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                            {/* File Icon based on content type */}
                            <Box sx={{ mr: 2 }}>
                              {doc.contentType?.includes('pdf') ? (
                                <PictureAsPdfIcon sx={{ fontSize: 24, color: 'error.main' }} />
                              ) : doc.contentType?.includes('image') ? (
                                <ImageIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                              ) : (
                                <InsertDriveFileIcon sx={{ fontSize: 24, color: 'info.main' }} />
                              )}
                            </Box>
                            
                            {/* File Name and Date */}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {doc.name || doc.type || 'Document'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {doc.uploadDate ? new Date(doc.uploadDate).toLocaleDateString() : ''}
                              </Typography>
                            </Box>
                            
                            {/* View and Download Icons */}
                            <Box>
                              <Tooltip title="View Document">
                                <Button
                                  size="small"
                                  variant="text"
                                  color="primary"
                                  startIcon={<DescriptionIcon />}
                                  onClick={() => {
                                    // Use the theme-tokenized MUI dialog viewer
                                    if (!doc?.url) {
                                      setSnackbar({ open: true, message: 'Document URL is missing', severity: 'error' });
                                      return;
                                    }
                                    viewDocument(doc.url, doc.name || 'Document');
                                  }}
                                >
                                  View
                                </Button>
                              </Tooltip>
                              <Tooltip title="Download Document">
                                <Button
                                  size="small"
                                  variant="text"
                                  color="primary"
                                  startIcon={<AttachmentIcon />}
                                  onClick={() => {
                                    const token = getToken();
                                    // Download file with auth header
                                    fetch(doc.url, {
                                      headers: {
                                        Authorization: `Bearer ${token}`
                                      }
                                    })
                                    .then(response => response.blob())
                                    .then(blob => {
                                      const url = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = doc.name || 'document';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    })
                                    .catch(error => {
                                      console.error('Error downloading document:', error);
                                      setSnackbar({
                                        open: true,
                                        message: 'Error downloading document',
                                        severity: 'error'
                                      });
                                    });
                                  }}
                                >
                                  Download
                                </Button>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))}
                        {(!workflowRequest.documents || workflowRequest.documents.length === 0) && (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              No documents available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                )}
                
                {/* Workflow Steps section has been moved to the Steps tab */}
              </Box>
            </Paper>
          </Grid>
        ) : null}
      </Grid>
      {snackbar.open && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      )}
      
      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerDialog.open}
        onClose={handleCloseFileViewer}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
            width: '95vw',
            maxWidth: '95vw',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" component="div">
            {fileViewerDialog.fileName || 'Document Viewer'}
          </Typography>
          <IconButton onClick={handleCloseFileViewer} size="small">
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 0 }}>
          {fileViewerDialog.loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading document...
              </Typography>
            </Box>
          ) : fileViewerDialog.error ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error" gutterBottom>
                Error Loading Document
              </Typography>
              <Typography variant="body1" paragraph>
                {fileViewerDialog.error}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Please try again or contact support if the problem persists.
              </Typography>
            </Box>
          ) : fileViewerDialog.fileType && fileViewerDialog.fileUrl ? (
            fileViewerDialog.fileType.includes('image') ? (
              <img 
                src={fileViewerDialog.fileUrl} 
                alt={fileViewerDialog.fileName} 
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
              />
            ) : fileViewerDialog.fileType.includes('pdf') ? (
              <object 
                data={fileViewerDialog.fileUrl} 
                type="application/pdf" 
                width="100%" 
                height="100%"
                style={{ border: 'none' }}
              >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" paragraph>
                    Unable to display PDF.
                  </Typography>
                  <Button 
                    variant="contained" 
                    href={fileViewerDialog.fileUrl} 
                    download={fileViewerDialog.fileName}
                  >
                    Download Instead
                  </Button>
                </Box>
              </object>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" paragraph>
                  This file type cannot be previewed directly.
                </Typography>
                <Button 
                  variant="contained" 
                  href={fileViewerDialog.fileUrl} 
                  download={fileViewerDialog.fileName}
                >
                  Download File
                </Button>
              </Box>
            )
          ) : (
            <Typography variant="body1" color="textSecondary">
              No document to display
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleCloseFileViewer} 
            variant="outlined"
          >
            Close
          </Button>
          {fileViewerDialog.fileUrl && !fileViewerDialog.loading && !fileViewerDialog.error && (
            <Button 
              variant="contained" 
              href={fileViewerDialog.fileUrl} 
              download={fileViewerDialog.fileName}
              startIcon={<AttachmentIcon />}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Document Viewer Modal */}
      <DocumentViewer
        open={documentViewerOpen}
        onClose={handleCloseDocumentViewer}
        document={currentDocument}
      />

      {/* Skip Step Confirmation Dialog */}
      <Dialog
        open={skipStepDialog.open}
        onClose={handleCloseSkipDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Skip Step Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to skip the following step?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontWeight: 'bold' }}>
            {skipStepDialog.stepName}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            This action cannot be undone. The step will be marked as skipped and will not be executed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseSkipDialog}
            variant="contained"
            disabled={loading}
            sx={{ minWidth: '100px', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSkipStep}
            variant="contained"
            color="warning"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ 
              minWidth: '120px',
              fontWeight: 600
            }}
          >
            {loading ? 'Skipping...' : 'Skip Step'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowRequestDetails;
